import { Component, OnInit, ViewChild, Input, AfterViewInit, ChangeDetectorRef, ElementRef, ViewChildren, QueryList, EventEmitter, Output, DoCheck } from '@angular/core';
import { IonModal, Platform } from '@ionic/angular';
import { PathGroup } from '../path-group/path-group.model';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { SessionService } from '../session/session.service';
import { AppState } from '../app.service';
import { I18n } from '../i18n.service';

@Component({
  selector: 'app-alert-path-select-v2',
  templateUrl: './alert-path-select-v2.component.html',
  styleUrls: ['./alert-path-select-v2.component.scss'],
   animations: [
    trigger('fadeInOut', [
      state('in', style({ opacity: 1, height: '*' })),
      transition(':enter', [
        style({ opacity: 0 }),
        animate('1s ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('0.2s ease-in', style({ opacity: 0, height: 0 }))
      ])
    ])
  ]
})
export class AlertPathSelectV2Component implements OnInit, AfterViewInit,DoCheck {
  modalState: 'open' | 'closed' = 'closed';
  @Input() data;
  pathGroups;
  /** Series sections, when the server sent any. Empty means the flat list. */
  seriesGroups: any[] = [];

  /** Which section the filter is showing: 'all', one Series id, or 'other'. */
  seriesFilter: any = 'all';

  /**
   * The filter's options, built once with the sections.
   *
   * A field rather than a getter: a getter hands the template a new array on every
   * change-detection pass, so the chip row is rebuilt on each one.
   */
  seriesFilterOptions: Array<{ id: any; name: string }> = [];

  /** The header on the phone's filter dialog. A field for the same reason. */
  filterInterfaceOptions: any = {};
  isTabletDevice;
  @ViewChild(IonModal, { static: true }) modal: IonModal;
  isHovered: boolean = false;
  rowHovered: boolean[] = [];
  @Output() pathSelected: EventEmitter<any> = new EventEmitter<any>();
  expandedPaths: { [key: string]: boolean } = {};
  isMobile;
  constructor(
    private platform: Platform,
    private cdr: ChangeDetectorRef,
    public sessionService: SessionService,
    private appState:AppState,
    public i18n: I18n
  ) { }

  ngOnInit() {
    this.pathGroups = this.data;
    this.isMobile = this.appState.get('isMobile');
    this.buildSeriesGroups();
  }

  /**
   * The Cycles on offer, grouped the way the server grouped them.
   *
   * Empty whenever the client has no Series — the flat list below is then exactly what it always
   * was. A Cycle in two Series is offered under both; selection keys on its id, so choosing it in
   * one place is choosing it everywhere.
   */
  private buildSeriesGroups(): void {
    const offered = new Set((this.pathGroups || []).map((path: any) => String(path?.id)));
    // Filtered against what was passed in: the caller decides which Cycles this screen offers, and
    // the grouping must not widen that.
    const keep = (paths: any[]) => (paths || []).filter((path: any) => offered.has(String(path?.id)));

    const groups = (this.appState.get('clientSeriesGroups') || [])
      // A disabled Series is a soft delete — it shows nowhere. The server omits them and lets their
      // Cycles fall through to the ungrouped list; this is here for the case where one arrives.
      .filter((series: any) => series?.enabled !== false)
      .map((series: any) => ({ ...series, paths: keep(series?.paths) }))
      .filter((series: any) => series.paths.length > 0)
      // The client-wide order, as the all-cycles screen uses. The server already sends them this
      // way, so this only matters if that ever stops being true — and a stable sort leaves an
      // already-ordered list alone.
      .sort((a: any, b: any) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));

    // Other is everything on offer that no surviving group claims — derived, not read from
    // clientUngroupedPaths. A Cycle whose only Series was disabled is in neither: not in a group,
    // because the group is gone, and not in the server's ungrouped list, because the server had it
    // in a Series. Reading only that list dropped it from the screen entirely, and the flat
    // fallback does not run while any group survives, so it could not be chosen at all.
    const claimed = new Set<string>();
    groups.forEach((group: any) => group.paths.forEach((path: any) => claimed.add(String(path?.id))));
    const ungrouped = (this.pathGroups || [])
      .filter((path: any) => path?.id != null && !claimed.has(String(path.id)));
    if (groups.length > 0 && ungrouped.length > 0) {
      groups.push({ id: 'other', name: this.i18n.t('Other'), paths: ungrouped });
    }
    this.seriesGroups = groups;

    this.seriesFilterOptions = groups.length > 0
      ? [{ id: 'all', name: this.i18n.t('All') },
         ...groups.map((group: any) => ({ id: group.id, name: group.name }))]
      : [];
    this.filterInterfaceOptions = { header: this.i18n.t('Filter Cycles') };
    // A filter left pointing at a Series this screen no longer offers would show nothing at all.
    if (!this.seriesFilterOptions.some((option) => option.id === this.seriesFilter)) {
      this.seriesFilter = 'all';
    }
  }

  /** Whether a section is on screen under the current filter. */
  showsSection(id: any): boolean {
    return this.seriesFilter === 'all' || this.seriesFilter === id;
  }

  selectSeriesFilter(id: any): void {
    this.seriesFilter = id;
  }

  /** Stable identity for the chip row, so a rebuild does not re-create every button. */
  trackFilterOption(_index: number, option: { id: any }) {
    return option?.id;
  }

  ngDoCheck(): void {
   this.isTabletDevice= this.platform.is("mobileweb") &&
      (this.platform.is("ipad") || this.platform.is("tablet"));
  }
  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  onSelect(path: any,pathGroup) {
    this.modal.dismiss({path:path,pathGroup:pathGroup }, 'success');
  }

  toggleDescription(path: any): void {
    this.expandedPaths[path.id] = !this.expandedPaths[path.id];
  }

  async openModal() {
    this.modalState = 'open';
  }

  closeModal() {
    this.modalState = 'closed';
  }
  c(index: number, hovered: boolean): void {
    this.rowHovered[index] = hovered;
  }
  
  cancel() {
    if (this.modal) {
      this.modal.dismiss(null, 'cancel');
    }
  }
  truncateText(text: string, maxLength: number) {
    if (text.length <= maxLength) return text;

    const truncated = text.slice(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    return truncated.slice(0, lastSpace);
  }

}
